import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaCheckCircle, FaAngleRight } from 'react-icons/fa';
import { 
  LuBrainCircuit, 
  LuLightbulb, 
  LuSettings, 
  LuBot, 
  LuActivity, 
  LuMap, 
  LuPiggyBank, 
  LuScale, 
  LuUsers, 
  LuMessageSquare, 
  LuTrendingUp, 
  LuFileText, 
  LuUserCog, 
  LuEye, 
  LuGauge, 
  LuRefreshCw, 
  LuDollarSign, 
  LuShieldCheck, 
  LuCircleArrowUp, 
  LuNetwork, 
  LuGitMerge, 
  LuPlug, 
  LuUserCheck, 
  LuGlobe,
  LuSparkles
} from 'react-icons/lu';
import SEO from '../components/SEO/SEO';
import contentData from '../data/contentData.json';

// Icon mapping based on IDs
const iconMap = {
  // Categories
  'ai-strategy': <LuBrainCircuit />,
  'ai-solution-dev': <LuLightbulb />,
  'ai-managed-services': <LuSettings />,
  'agentic-ai': <LuBot />,

  // AI Strategy Services
  'ai-readiness': <LuActivity />,
  'enterprise-ai-roadmap': <LuMap />,
  'roi-modelling': <LuPiggyBank />,
  'ethical-ai': <LuScale />,
  'executive-workshops': <LuUsers />,

  // Solution Dev Services
  'intelligent-chatbots': <LuMessageSquare />,
  'predictive-analytics': <LuTrendingUp />,
  'document-intelligence': <LuFileText />,
  'personalization-engines': <LuUserCog />,
  'computer-vision-nlp': <LuEye />,

  // Managed Services
  'monitoring-tuning': <LuGauge />,
  'model-lifecycle': <LuRefreshCw />,
  'cost-optimization': <LuDollarSign />,
  'security-compliance': <LuShieldCheck />,
  'continuous-improvement': <LuCircleArrowUp />,

  // Agentic AI Services
  'multi-agent-architectures': <LuNetwork />,
  'autonomous-workflow': <LuGitMerge />,
  'enterprise-integration': <LuPlug />,
  'human-in-the-loop': <LuUserCheck />,
  'scalable-ecosystems': <LuGlobe />
};

const getIcon = (id, fallback) => {
  return iconMap[id] || <span className="text-2xl">{fallback}</span>;
};

const AIServicesHubPage = () => {
  const [activeCategoryIndex, setActiveCategoryIndex] = useState(0);

  const seoData = {
    title: "Applied & Generative AI Services | Dhyon Technologies",
    description: "Transforming businesses with intelligent, scalable, and responsible AI solutions. Explore our AI Strategy, Development, Managed Services, and Agentic AI offerings.",
    keywords: "AI Services, Generative AI, AI Strategy, AI Consulting, Agentic AI, Dhyon Technologies"
  };

  const categories = contentData.aiServices || [];
  const activeCategory = categories[activeCategoryIndex];

  return (
    <div className="bg-white min-h-screen font-sans selection:bg-purple-100 selection:text-purple-900">
      <SEO {...seoData} />

      {/* Hero Section */}
      <section className="relative pt-40 pb-20 bg-[#0F0524] overflow-hidden text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-blue-900/40 via-[#0F0524] to-[#0F0524]"></div>
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5"></div>
        
        <div className="container relative z-10 px-6 mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <div className="inline-block p-3 mb-6 rounded-full bg-white/10 backdrop-blur-md border border-white/20">
               <span className="text-2xl flex items-center justify-center text-blue-400">
                 <LuSparkles className="w-8 h-8" />
               </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight leading-tight mb-6 bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              Applied & Generative AI Services
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 leading-relaxed font-light max-w-3xl mx-auto">
              Transforming businesses with intelligent, scalable, and responsible AI solutions
            </p>
            
            <div className="mt-10">
              <Link 
                to="/contact" 
                className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full font-bold hover:shadow-lg hover:shadow-purple-500/30 transition-all transform hover:-translate-y-1"
              >
                Talk to AI Experts
                <FaArrowRight className="ml-2" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="flex flex-col lg:flex-row gap-12 bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100 min-h-[800px]">
            
            {/* Left Sidebar - Navigation */}
            <div className="w-full lg:w-[350px] bg-gray-50 border-r border-gray-100 p-8 flex flex-col">
              <h3 className="text-xl font-bold text-gray-900 mb-8 px-2">Service Categories</h3>
              <div className="space-y-3">
                {categories.map((category, index) => (
                  <button
                    key={category.id || index}
                    onClick={() => setActiveCategoryIndex(index)}
                    className={`w-full text-left p-4 rounded-xl transition-all duration-300 flex items-center justify-between group ${
                      activeCategoryIndex === index
                        ? 'bg-white shadow-md border border-purple-100 ring-1 ring-purple-50'
                        : 'hover:bg-white hover:shadow-sm border border-transparent'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-xl transition-colors ${
                        activeCategoryIndex === index 
                          ? 'bg-purple-100 text-purple-600' 
                          : 'bg-gray-200 text-gray-500 group-hover:bg-gray-300'
                      }`}>
                        {getIcon(category.id, category.icon)}
                      </div>
                      <span className={`font-semibold ${
                        activeCategoryIndex === index ? 'text-gray-900' : 'text-gray-600 group-hover:text-gray-900'
                      }`}>
                        {category.category}
                      </span>
                    </div>
                    {activeCategoryIndex === index && (
                      <FaAngleRight className="text-purple-600" />
                    )}
                  </button>
                ))}
              </div>
              
              {/* Optional Sidebar Info */}
              <div className="mt-auto pt-10 px-2">
                <div className="bg-blue-50/50 rounded-xl p-6 border border-blue-100">
                  <h4 className="font-bold text-gray-900 mb-2">Need guidance?</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Not sure where to start with AI? Our experts can help assess your readiness.
                  </p>
                  <Link to="/contact" className="text-sm font-bold text-blue-600 hover:text-blue-700 flex items-center">
                    Book a Free Assessments <FaArrowRight className="ml-1 text-xs" />
                  </Link>
                </div>
              </div>
            </div>

            {/* Right Content Area */}
            <div className="flex-1 p-8 lg:p-12">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeCategory.id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Category Header */}
                  <div className="mb-10 pb-8 border-b border-gray-100">
                    <div className="flex items-center gap-4 mb-4">
                      <span className="text-4xl text-purple-600">
                        {getIcon(activeCategory.id, activeCategory.icon)}
                      </span>
                      <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                        {activeCategory.category}
                      </h2>
                    </div>
                    <p className="text-xl text-gray-600 leading-relaxed max-w-4xl">
                      {activeCategory.description}
                    </p>
                    
                    {activeCategory.overallOutcome && (
                       <div className="mt-6 inline-flex items-center gap-2 bg-green-50 text-green-800 px-5 py-2 rounded-full text-sm font-medium border border-green-100">
                          <FaCheckCircle className="text-green-500" />
                          <span>Goal: {activeCategory.overallOutcome}</span>
                       </div>
                    )}
                  </div>

                  {/* Services Grid */}
                  <div className="grid md:grid-cols-2 gap-6">
                    {activeCategory.services.map((service, idx) => (
                      <div 
                        key={service.id || idx}
                        className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md hover:border-blue-100 transition-all duration-300 group"
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center text-2xl shrink-0 group-hover:scale-110 transition-transform duration-300 text-blue-600">
                            {getIcon(service.id, service.icon)}
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                              {service.title}
                            </h3>
                            <p className="text-gray-600 text-sm leading-relaxed mb-4">
                              {service.description}
                            </p>
                            {service.outcome && (
                              <div className="text-xs bg-gray-50 text-gray-700 p-3 rounded-lg border border-gray-100">
                                <span className="font-semibold text-blue-600 block mb-1">Outcome:</span>
                                {service.outcome}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Category Specific CTA */}
                  <div className="mt-12 bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 text-white flex flex-col md:flex-row items-center justify-between gap-6 shadow-lg">
                    <div>
                      <h3 className="text-xl font-bold mb-2">Ready to implement {activeCategory.category}?</h3>
                      <p className="text-gray-300 text-sm">Let's discuss how we can tailor this to your business needs.</p>
                    </div>
                    <Link 
                      to="/contact"
                      className="px-6 py-3 bg-white text-gray-900 rounded-xl font-bold hover:bg-gray-100 transition-colors shadow-sm whitespace-nowrap"
                    >
                      Get Started
                    </Link>
                  </div>

                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

       {/* FAQ / Bottom Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold mb-12">Why Choose Dhyon for AI?</h2>
            <div className="grid md:grid-cols-3 gap-8">
               {[
                 { title: "Enterprise-Grade Security", desc: "We ensure your AI solutions are secure, compliant, and built for enterprise scale.", icon: <LuShieldCheck /> },
                 { title: "RoI-Focused Approach", desc: "We move beyond hype to deliver measurable business value and return on investment.", icon: <LuTrendingUp /> },
                 { title: "End-to-End Expertise", desc: "From strategy to development to managed services, we handle the entire AI lifecycle.", icon: <LuRefreshCw /> }
               ].map((item, i) => (
                  <div key={i} className="p-6 rounded-2xl bg-gray-50 border border-gray-100 items-center flex flex-col">
                     <div className="text-4xl mb-4 text-purple-600">{item.icon}</div>
                     <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                     <p className="text-gray-600">{item.desc}</p>
                  </div>
               ))}
            </div>
        </div>
      </section>
    </div>
  );
};

export default AIServicesHubPage;
